import { ReactNode, useEffect, useRef, useState } from "react";
import { ArrowDownToLine, ChevronDown } from "lucide-react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { Button } from "@/components/shadcn/button";

interface Props {
  defaultCollapsed?: boolean;
  children: ReactNode;
  output_url?: string;
  min_height?: number;
  fadeToClassName?: string;
}

interface DownloadLinkProps {
  href?: string | null;
}
const DownloadLink: React.FC<DownloadLinkProps> = ({ href }) => {
  if (!href) return <></>;
  return (
    <div className="flex h-10 items-end">
      <div className="text-xs">
        <span className="inline">Results are to big to show. You can </span>
        <Link
          href={href}
          className="inline"
          target="_blank"
          rel="noopener noreferrer"
        >
          <ArrowDownToLine size="1rem" className="inline align-middle" />
        </Link>
        <Link
          href={href}
          className="hover:underline inline font-semibold"
          target="_blank"
          rel="noopener noreferrer"
        >
          download them as a txt.
        </Link>
      </div>
    </div>
  );
};

export const ExpandableContent = ({
  defaultCollapsed = true,
  children,
  output_url,
  min_height = 1000,
  fadeToClassName = "to-background",
}: Props) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const handleToggle = () => setIsCollapsed(!isCollapsed);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const currentBox = boxRef.current;
    if (!currentBox) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setChildrenHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(currentBox);

    return () => {
      resizeObserver.unobserve(currentBox);
      resizeObserver.disconnect();
    };
  }, []);

  const [childrenHeight, setChildrenHeight] = useState(0);

  const shouldDisplayButton = childrenHeight > min_height;
  const shouldDisplayFade =
    !!output_url || (shouldDisplayButton && isCollapsed);

  return (
    <div className="relative w-full min-w-0 max-w-full">
      <div
        className="overflow-hidden w-full min-w-0"
        style={{
          maxHeight: isCollapsed
            ? `${min_height}px`
            : `${childrenHeight || 2000}px`,
          transition: "max-height 0.5s",
        }}
      >
        <div ref={boxRef} className="w-full min-w-0">
          {children}
        </div>
      </div>
      {shouldDisplayButton && !output_url && (
        <Button
          className="mt-3 px-2 -ml-2"
          variant="ghost"
          onClick={handleToggle}
        >
          <ChevronDown
            size="1.25rem"
            strokeWidth="1.25px"
            style={{
              transform: isCollapsed ? "rotate(0deg)" : "rotate(-180deg)",
              transition: "all 0.125s linear",
            }}
          />
          {isCollapsed ? "Read more" : "Show less"}
        </Button>
      )}
      {output_url && <DownloadLink href={output_url} />}
      {shouldDisplayFade && (
        <div
          className={cn(
            "pointer-events-none absolute bottom-10 left-0 right-0 h-10 w-full bg-gradient-to-b from-transparent",
            fadeToClassName
          )}
          aria-hidden
        />
      )}
    </div>
  );
};
